import { useQueryClient, useMutation, UseMutationResult } from 'react-query';

import {
    withHeaders,
    instance as axiosInstance,
} from 'core/util/axiosInstance';
import { getDefaultHeaders } from 'core/util/other';
import { AxiosResponse } from 'axios';
import { useAlert } from 'core/contexts';
import { commonShowAlert } from 'lib';
interface CreateConsentContract {
    consentContract: {
        to?: string[] | '*';
        jwtProperties?: any;
        start: string;
        until: string;
        when: string;
        only: string[];
        description: string;
    };
}
interface PutConsentContractProps {
    id: string;
    contract: CreateConsentContract;
    chain: string;
    token: string;
}

interface DeleteConsentContractProps {
    id: string;
    chain: string;
    token: string;
}

interface PostConsentContractProps {
    contract: CreateConsentContract;
    chain: string;
    token: string;
}

export const postConsentContract = (
    props: PostConsentContractProps
): Promise<AxiosResponse> => {
    const headers = getDefaultHeaders(props.token);
    const url = `/graphchain/${props.chain}/consentcontracts`;

    return axiosInstance().post(url, props.contract, withHeaders(headers));
};

export const putConsentContract = (
    props: PutConsentContractProps
): Promise<AxiosResponse> => {
    const headers = getDefaultHeaders(props.token);
    const url = `/graphchain/${props.chain}/consentcontracts/${props.id}`;
    return axiosInstance().put(url, props.contract, withHeaders(headers));
};

export const deleteConsentContract = (
    props: DeleteConsentContractProps
): Promise<AxiosResponse> => {
    const headers = getDefaultHeaders(props.token);
    const url = `/graphchain/${props.chain}/${props.id}`;

    return axiosInstance().delete(url, withHeaders(headers));
};

export const useCreateConsentContract = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    PostConsentContractProps,
    unknown
> => {
    const queryClient = useQueryClient();
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(postConsentContract, {
        onSuccess: () => {
            queryClient.invalidateQueries(`consent-contracts`);
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Consent Contract Created'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to create consent contract';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useUpdateConsentContract = (): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    PutConsentContractProps,
    unknown
> => {
    const queryClient = useQueryClient();
    const { showAlert, hideAlert, showErrorDetails } = useAlert();

    return useMutation(putConsentContract, {
        onSuccess: () => {
            queryClient.invalidateQueries('consent-contracts');
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Consent contract updated'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to update consent contract';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};

export const useDeleteConsentContract = (
    props: any
): UseMutationResult<
    AxiosResponse<any, any>,
    any,
    DeleteConsentContractProps,
    unknown
> => {
    const queryClient = useQueryClient();
    const { showAlert, hideAlert, showErrorDetails } = useAlert();
    const { chain } = props;

    return useMutation(deleteConsentContract, {
        onSuccess: () => {
            queryClient.invalidateQueries(`consent-contracts-${chain}`);
            commonShowAlert(
                showAlert,
                hideAlert,
                'success',
                'Consent contract deleted'
            );
        },
        onError: (error: any) => {
            const errorMessage: string =
                error?.data?.message ?? 'Unable to delete consent contract';
            commonShowAlert(
                showAlert,
                hideAlert,
                'error',
                errorMessage,
                JSON.stringify(error?.data ?? ''),
                showErrorDetails
            );
        },
    });
};
